import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Button,
  Link
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Accessible as AccessibleIcon,
  Visibility as VisibilityIcon,
  Hearing as HearingIcon,
  TouchApp as TouchAppIcon,
  Keyboard as KeyboardIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Web as WebIcon,
  Settings as SettingsIcon,
  Feedback as FeedbackIcon,
  Support as SupportIcon,
  Update as UpdateIcon,
  Assessment as AssessmentIcon,
  Help as HelpIcon,
  VerifiedUser as VerifiedUserIcon,
  ContactSupport as ContactSupportIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

function Accessibility() {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Smooth scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const sections = [
    {
      id: 'intro',
      title: 'Our Commitment',
      icon: <AccessibleIcon />,
      color: '#1976d2'
    },
    {
      id: 'standards',
      title: 'Accessibility Standards',
      icon: <VerifiedUserIcon />,
      color: '#388e3c'
    },
    {
      id: 'features',
      title: 'Accessibility Features',
      icon: <SettingsIcon />,
      color: '#f57c00'
    },
    {
      id: 'visual',
      title: 'Visual Accessibility',
      icon: <VisibilityIcon />,
      color: '#7b1fa2'
    },
    {
      id: 'hearing',
      title: 'Hearing Accessibility',
      icon: <HearingIcon />,
      color: '#d32f2f'
    },
    {
      id: 'keyboard',
      title: 'Keyboard Navigation',
      icon: <KeyboardIcon />,
      color: '#0288d1'
    },
    {
      id: 'assistive',
      title: 'Assistive Technologies',
      icon: <TouchAppIcon />,
      color: '#e64a19'
    },
    {
      id: 'mobile',
      title: 'Mobile Accessibility',
      icon: <PhoneAndroidIcon />,
      color: '#00796b'
    },
    {
      id: 'third-party',
      title: 'Third-Party Content',
      icon: <WebIcon />,
      color: '#5e35b1'
    },
    {
      id: 'testing',
      title: 'Testing & Evaluation',
      icon: <AssessmentIcon />,
      color: '#c2185b'
    },
    {
      id: 'feedback',
      title: 'Feedback & Support',
      icon: <FeedbackIcon />,
      color: '#795548'
    },
    {
      id: 'improvements',
      title: 'Ongoing Improvements',
      icon: <UpdateIcon />,
      color: '#607d8b'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4, pt: 12 }}>
      {/* Header */}
      <Box sx={{ mb: 8, textAlign: 'center' }}>
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold', 
            color: '#1976d2',
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 3
          }}
        >
          Circledin Accessibility Statement
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Effective Date: January 1, 2025 | Last Updated: January 1, 2025 | Version: 1.0
        </Typography>
        <Chip 
          label="Current Version" 
          color="primary" 
          sx={{ 
            mt: 1,
            fontSize: '0.875rem',
            fontWeight: 'bold',
            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
          }} 
        />
      </Box>

      {/* Quick Overview */}
      <Paper 
        elevation={6} 
        sx={{ 
          p: 5, 
          mb: 6, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          color: 'white',
          borderRadius: '16px',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Accessibility for Everyone
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mb: 4, opacity: 0.9 }}>
          We are committed to ensuring our platform is accessible to users with disabilities and providing an inclusive experience for all.
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                  WCAG 2.1 AA
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                  Compliant with international accessibility standards
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                  Universal Design
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                  Built with accessibility in mind from the ground up
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                  Continuous Improvement
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                  Regular testing and updates for better accessibility
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Important Notice */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 5, 
          p: 3,
          borderRadius: '12px',
          border: '1px solid #2196f3',
          boxShadow: '0 4px 16px rgba(33, 150, 243, 0.1)'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          Need Accessibility Assistance?
        </Typography>
        <Typography variant="body1">
          If you experience any difficulty accessing our content or features, please contact us at <Link href="mailto:accessibility@circledin.com">accessibility@circledin.com</Link> and we will work to address your concerns.
        </Typography>
      </Alert>

      {/* Table of Contents */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          mb: 5,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}
      >
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            fontWeight: 'bold',
            color: '#1976d2',
            mb: 3
          }}
        >
          <HelpIcon sx={{ fontSize: '2rem' }} /> Table of Contents
        </Typography>
        <Grid container spacing={3}>
          {sections.map((section, index) => (
            <Grid item xs={12} sm={6} md={4} key={section.id}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={section.icon}
                onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' })}
                sx={{ 
                  justifyContent: 'flex-start', 
                  color: section.color, 
                  borderColor: section.color,
                  borderRadius: '12px',
                  p: 2,
                  height: 'auto',
                  textAlign: 'left',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: `${section.color}10`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 16px ${section.color}30`
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                    {index + 1}. {section.title}
                  </Typography>
                </Box>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Accessibility Content */}
      <Box sx={{ mb: 6 }}>
        
        {/* Section 1: Our Commitment */}
        <Accordion 
          expanded={expanded === 'panel1'} 
          onChange={handleChange('panel1')} 
          id="intro"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <AccessibleIcon sx={{ mr: 2, color: '#1976d2' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>1. Our Accessibility Commitment</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography paragraph>
              At Circledin, we believe that technology should be accessible to everyone, regardless of ability or disability. We are committed to providing an inclusive digital experience that allows all users to effectively access our mobile plan sharing platform.
            </Typography>
            <Typography paragraph>
              Our accessibility efforts are guided by the principle that digital inclusion is not just a compliance requirement, but a fundamental aspect of good design and social responsibility. We strive to create an environment where users with disabilities can independently navigate, understand, and interact with our services.
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Our Accessibility Principles</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Perceivable" 
                  secondary="Information and user interface components must be presentable to users in ways they can perceive"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Operable" 
                  secondary="User interface components and navigation must be operable by all users"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Understandable" 
                  secondary="Information and operation of user interface must be understandable"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Robust" 
                  secondary="Content must be robust enough to be interpreted by a wide variety of user agents and assistive technologies"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 2: Accessibility Standards */}
        <Accordion 
          expanded={expanded === 'panel2'} 
          onChange={handleChange('panel2')} 
          id="standards"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <VerifiedUserIcon sx={{ mr: 2, color: '#388e3c' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>2. Accessibility Standards Compliance</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Web Content Accessibility Guidelines (WCAG)</Typography>
            <Typography paragraph>
              Our website and mobile application are designed to meet the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. These internationally recognized guidelines provide a framework for making web content more accessible to people with disabilities.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Standards We Follow</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="WCAG 2.1 Level AA" 
                  secondary="International standard for web accessibility"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Section 508" 
                  secondary="U.S. federal accessibility requirements"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="ADA Compliance" 
                  secondary="Americans with Disabilities Act requirements"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="EN 301 549" 
                  secondary="European accessibility standard for ICT products and services"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Accessibility Features Implementation</Typography>
            <Typography paragraph>We implement accessibility features including:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Semantic HTML markup for proper content structure" />
              </ListItem>
              <ListItem>
                <ListItemText primary="ARIA (Accessible Rich Internet Applications) labels and descriptions" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Color contrast ratios meeting WCAG AA standards (4.5:1)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Keyboard navigation support for all interactive elements" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Alternative text for images and visual content" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Descriptive headings and content organization" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 3: Accessibility Features */}
        <Accordion 
          expanded={expanded === 'panel3'} 
          onChange={handleChange('panel3')} 
          id="features"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <SettingsIcon sx={{ mr: 2, color: '#f57c00' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>3. Built-in Accessibility Features</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Navigation and Structure</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Skip Links" 
                  secondary="Quick navigation to main content areas for keyboard and screen reader users"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Logical Tab Order" 
                  secondary="Intuitive keyboard navigation sequence throughout all pages"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Landmark Regions" 
                  secondary="Properly marked navigation, main content, and complementary areas"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Breadcrumb Navigation" 
                  secondary="Clear location indicators to help users understand their position"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Interactive Elements</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Focus Indicators" 
                  secondary="Clear visual indicators when elements receive keyboard focus"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Button and Link Labels" 
                  secondary="Descriptive text that explains the purpose of each interactive element"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Form Labels and Instructions" 
                  secondary="Clear labeling and guidance for all form inputs"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Error Identification" 
                  secondary="Clear error messages and correction guidance"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Content Organization</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Hierarchical Headings" 
                  secondary="Properly structured H1-H6 headings for content organization"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="List Structures" 
                  secondary="Appropriate use of ordered and unordered lists"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Data Tables" 
                  secondary="Properly structured tables with headers and captions"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Content Grouping" 
                  secondary="Related content grouped logically with appropriate markup"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 4: Visual Accessibility */}
        <Accordion 
          expanded={expanded === 'panel4'} 
          onChange={handleChange('panel4')} 
          id="visual"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <VisibilityIcon sx={{ mr: 2, color: '#7b1fa2' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>4. Visual Accessibility</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Color and Contrast</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="High Contrast Ratios" 
                  secondary="Text meets WCAG AA contrast requirements (4.5:1 for normal text, 3:1 for large text)"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Color Independence" 
                  secondary="Information is not conveyed by color alone; additional indicators are provided"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Color Blind Friendly" 
                  secondary="Design considerations for users with various types of color vision deficiencies"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="User-Controlled Contrast" 
                  secondary="Compatibility with high contrast mode and user style sheets"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Typography and Layout</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Scalable Text" 
                  secondary="Text can be enlarged up to 200% without loss of functionality"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Readable Fonts" 
                  secondary="Clear, legible typography with adequate line spacing"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Flexible Layout" 
                  secondary="Content reflows appropriately when text is enlarged"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Visual Hierarchy" 
                  secondary="Clear visual organization with appropriate use of whitespace"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Images and Media</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Alternative Text" 
                  secondary="Descriptive alt text for all meaningful images"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Decorative Images" 
                  secondary="Properly marked decorative images that don't convey content"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Complex Images" 
                  secondary="Long descriptions for charts, graphs, and complex visual content"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Image Text" 
                  secondary="Minimal use of text in images; alternative text provided when used"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 5: Hearing Accessibility */}
        <Accordion 
          expanded={expanded === 'panel5'} 
          onChange={handleChange('panel5')} 
          id="hearing"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <HearingIcon sx={{ mr: 2, color: '#d32f2f' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>5. Hearing Accessibility</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Audio Content</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Video Captions" 
                  secondary="Closed captions provided for all video content with dialogue or important audio"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Audio Descriptions" 
                  secondary="Descriptive audio tracks for visual content in videos when necessary"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Transcripts" 
                  secondary="Text transcripts available for audio-only content"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Auto-playing Media Control" 
                  secondary="Users can control auto-playing audio and video content"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Alternative Communication</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Visual Alerts" 
                  secondary="Visual indicators for system notifications and alerts"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Text-Based Support" 
                  secondary="Chat and email support options available as alternatives to phone support"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Visual Feedback" 
                  secondary="Visual confirmation for form submissions and user actions"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 6: Keyboard Navigation */}
        <Accordion 
          expanded={expanded === 'panel6'} 
          onChange={handleChange('panel6')} 
          id="keyboard"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <KeyboardIcon sx={{ mr: 2, color: '#0288d1' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>6. Keyboard Navigation Support</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Full Keyboard Accessibility</Typography>
            <Typography paragraph>
              Our platform is fully navigable using only a keyboard, ensuring users who cannot use a mouse or pointing device can access all functionality.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Keyboard Navigation Features</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Tab Navigation" 
                  secondary="Logical tab order through all interactive elements"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Skip Links" 
                  secondary="Quick navigation to main content and primary navigation areas"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Focus Management" 
                  secondary="Proper focus handling in modal dialogs and dynamic content"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Keyboard Shortcuts" 
                  secondary="Standard keyboard shortcuts for common actions where appropriate"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Standard Keyboard Commands</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Tab / Shift+Tab" 
                  secondary="Navigate forward/backward through interactive elements"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Enter / Space" 
                  secondary="Activate buttons and links"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Arrow Keys" 
                  secondary="Navigate within menus, lists, and other structured content"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Escape" 
                  secondary="Close modal dialogs and dropdown menus"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Focus Indicators</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Visible Focus" 
                  secondary="Clear visual indicators show which element has keyboard focus"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="High Contrast Focus" 
                  secondary="Focus indicators meet contrast requirements and are clearly visible"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Custom Focus Styles" 
                  secondary="Distinctive focus styles that complement the design while maintaining visibility"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 7: Assistive Technologies */}
        <Accordion 
          expanded={expanded === 'panel7'} 
          onChange={handleChange('panel7')} 
          id="assistive"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <TouchAppIcon sx={{ mr: 2, color: '#e64a19' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>7. Assistive Technology Compatibility</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Screen Reader Support</Typography>
            <Typography paragraph>
              Our platform is optimized to work with popular screen readers including:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="NVDA (NonVisual Desktop Access)" 
                  secondary="Free screen reader for Windows"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="JAWS (Job Access With Speech)" 
                  secondary="Professional screen reader for Windows"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="VoiceOver" 
                  secondary="Built-in screen reader for macOS and iOS"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="TalkBack" 
                  secondary="Built-in screen reader for Android devices"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Dragon NaturallySpeaking" 
                  secondary="Voice recognition software compatibility"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Screen Reader Optimization</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Semantic Markup" 
                  secondary="Proper HTML elements and ARIA labels for accurate screen reader interpretation"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Live Regions" 
                  secondary="Dynamic content updates announced to screen readers"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Descriptive Links" 
                  secondary="Link text that makes sense out of context"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Form Labels" 
                  secondary="Proper association between form controls and their labels"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Other Assistive Technologies</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Voice Control Software" 
                  secondary="Compatibility with voice navigation and control systems"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Switch Navigation" 
                  secondary="Support for single-switch and multiple-switch navigation devices"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Eye-Tracking Systems" 
                  secondary="Compatible with eye-gaze control systems"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Magnification Software" 
                  secondary="Works with screen magnification tools and browser zoom"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 8: Mobile Accessibility */}
        <Accordion 
          expanded={expanded === 'panel8'} 
          onChange={handleChange('panel8')} 
          id="mobile"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <PhoneAndroidIcon sx={{ mr: 2, color: '#00796b' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>8. Mobile Accessibility</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Mobile Platform Support</Typography>
            <Typography paragraph>
              Our mobile application and responsive website are designed to work with built-in accessibility features on both iOS and Android platforms.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>iOS Accessibility Features</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="VoiceOver Support" 
                  secondary="Full compatibility with iOS screen reader"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Dynamic Type" 
                  secondary="Text scales with user's preferred text size settings"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Voice Control" 
                  secondary="Support for iOS voice navigation commands"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Switch Control" 
                  secondary="Compatibility with external switch devices"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Reduce Motion" 
                  secondary="Respects user preferences for reduced motion and animations"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Android Accessibility Features</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="TalkBack Support" 
                  secondary="Full compatibility with Android screen reader"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Font Size Scaling" 
                  secondary="Adapts to user's preferred font size settings"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="High Contrast Mode" 
                  secondary="Enhanced visibility with system high contrast settings"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Voice Access" 
                  secondary="Support for Android voice control features"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Select to Speak" 
                  secondary="Text-to-speech functionality for selected content"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Touch and Gesture Accessibility</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Touch Target Size" 
                  secondary="Minimum 44x44 point touch targets for easy interaction"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Gesture Alternatives" 
                  secondary="Alternative ways to access swipe and gesture-controlled features"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Drag and Drop Support" 
                  secondary="Accessible drag and drop interactions with keyboard alternatives"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 9: Third-Party Content */}
        <Accordion 
          expanded={expanded === 'panel9'} 
          onChange={handleChange('panel9')} 
          id="third-party"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <WebIcon sx={{ mr: 2, color: '#5e35b1' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>9. Third-Party Content Accessibility</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Third-Party Service Requirements</Typography>
            <Typography paragraph>
              We work with third-party service providers and require them to maintain accessibility standards where possible. However, some third-party content may not be fully under our control.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Payment Processing</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Stripe Payment Forms" 
                  secondary="Payment processing forms designed with accessibility in mind"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Alternative Payment Methods" 
                  secondary="Multiple payment options to accommodate different user needs"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Embedded Content</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Social Media Widgets" 
                  secondary="Accessible alternatives provided where third-party widgets may not be fully accessible"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Maps and Location Services" 
                  secondary="Alternative text-based location information provided"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Analytics and Tracking" 
                  secondary="Accessibility considerations in analytics implementation"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Vendor Accessibility Standards</Typography>
            <Typography paragraph>
              We evaluate third-party vendors based on their accessibility compliance and work with them to ensure:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="WCAG Compliance" 
                  secondary="Third-party services meet or work toward WCAG 2.1 AA standards"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Accessibility Documentation" 
                  secondary="Vendors provide accessibility conformance statements and documentation"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Regular Accessibility Reviews" 
                  secondary="Ongoing evaluation of third-party accessibility implementations"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 10: Testing and Evaluation */}
        <Accordion 
          expanded={expanded === 'panel10'} 
          onChange={handleChange('panel10')} 
          id="testing"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <AssessmentIcon sx={{ mr: 2, color: '#c2185b' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>10. Accessibility Testing and Evaluation</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Regular Accessibility Audits</Typography>
            <Typography paragraph>
              We conduct regular accessibility evaluations to ensure our platform maintains high accessibility standards.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Testing Methods</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Automated Testing" 
                  secondary="Regular automated scans using accessibility testing tools"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Manual Testing" 
                  secondary="Human evaluation of accessibility features and user experience"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Screen Reader Testing" 
                  secondary="Testing with multiple screen readers to ensure compatibility"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Keyboard Navigation Testing" 
                  secondary="Comprehensive keyboard-only navigation testing"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="User Testing with Disabilities" 
                  secondary="Testing with users who rely on assistive technologies"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Testing Tools</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="WAVE (Web Accessibility Evaluation Tool)" 
                  secondary="Browser extension for accessibility evaluation"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="axe-core" 
                  secondary="Automated accessibility testing engine"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Lighthouse Accessibility Audit" 
                  secondary="Google's accessibility testing integrated into development workflow"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Color Contrast Analyzers" 
                  secondary="Tools to verify color contrast ratios meet WCAG standards"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Continuous Monitoring</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Development Integration" 
                  secondary="Accessibility testing integrated into development and deployment processes"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Regular Review Cycles" 
                  secondary="Quarterly comprehensive accessibility reviews"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Issue Tracking" 
                  secondary="Systematic tracking and resolution of accessibility issues"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 11: Feedback and Support */}
        <Accordion 
          expanded={expanded === 'panel11'} 
          onChange={handleChange('panel11')} 
          id="feedback"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <FeedbackIcon sx={{ mr: 2, color: '#795548' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>11. Accessibility Feedback and Support</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>How to Report Accessibility Issues</Typography>
            <Typography paragraph>
              We welcome feedback about the accessibility of our platform. If you encounter an accessibility barrier, please let us know so we can address it.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Contact Methods</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Email" 
                  secondary="accessibility@circledin.com - Dedicated accessibility support email"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Phone" 
                  secondary="1-800-CIRCLED (1-800-247-2533) - Accessible customer support line"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Online Form" 
                  secondary="Accessible contact form available on our website"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="TTY/TDD" 
                  secondary="Text telephone support available upon request"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>What to Include in Your Report</Typography>
            <Typography paragraph>
              To help us address accessibility issues quickly and effectively, please include:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Description of the Issue" 
                  secondary="Detailed explanation of the accessibility barrier you encountered"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Location of the Issue" 
                  secondary="Specific page URL or section where the issue occurred"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Assistive Technology Used" 
                  secondary="Screen reader, browser, or other assistive technology details"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Operating System and Browser" 
                  secondary="Technical details about your computing environment"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Response Timeline</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Acknowledgment" 
                  secondary="We will acknowledge receipt of your accessibility feedback within 2 business days"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Initial Response" 
                  secondary="Detailed response and action plan within 5 business days"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Resolution Timeline" 
                  secondary="Critical issues addressed within 10 business days; other issues within 30 days"
                />
              </ListItem>
            </List>

            <Alert severity="success" sx={{ mt: 3, borderRadius: '8px' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Alternative Access Assistance:
              </Typography>
              <Typography variant="body2">
                If you need immediate assistance accessing our services while we work on resolving an accessibility issue, please contact our customer support team who can provide alternative ways to complete your tasks.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Section 12: Ongoing Improvements */}
        <Accordion 
          expanded={expanded === 'panel12'} 
          onChange={handleChange('panel12')} 
          id="improvements"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <UpdateIcon sx={{ mr: 2, color: '#607d8b' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>12. Ongoing Accessibility Improvements</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Continuous Improvement Process</Typography>
            <Typography paragraph>
              Accessibility is an ongoing commitment. We continuously work to improve the accessibility of our platform through regular updates, user feedback, and technological advances.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Current Improvement Initiatives</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Enhanced Screen Reader Support" 
                  secondary="Ongoing improvements to ARIA implementation and semantic markup"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Voice Interface Development" 
                  secondary="Exploring voice control and dictation capabilities"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="AI-Powered Accessibility" 
                  secondary="Investigating AI tools for automatic alt text generation and content optimization"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Mobile Accessibility Enhancement" 
                  secondary="Continued improvement of mobile app accessibility features"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Training and Awareness</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Staff Training" 
                  secondary="Regular accessibility training for all development and design team members"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Accessibility Guidelines" 
                  secondary="Internal development guidelines that prioritize accessibility"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Industry Best Practices" 
                  secondary="Staying current with accessibility standards and emerging best practices"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Future Accessibility Goals</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="WCAG 2.2 Compliance" 
                  secondary="Upgrading to meet the latest WCAG standards as they become finalized"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Personalization Features" 
                  secondary="User-customizable accessibility preferences and settings"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Multi-language Accessibility" 
                  secondary="Ensuring accessibility features work across all supported languages"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Community Partnerships" 
                  secondary="Collaborating with disability advocacy organizations for feedback and improvement"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Accessibility Roadmap</Typography>
            <Typography paragraph>
              Our accessibility roadmap includes:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Q1 2025" 
                  secondary="Enhanced keyboard navigation and focus management improvements"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Q2 2025" 
                  secondary="Mobile app accessibility enhancements and testing expansion"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Q3 2025" 
                  secondary="Voice interface beta testing and implementation"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Q4 2025" 
                  secondary="Comprehensive accessibility audit and WCAG 2.2 compliance assessment"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Footer */}
      <Box sx={{ 
        mt: 8, 
        textAlign: 'center', 
        borderTop: '2px solid #e3f2fd', 
        pt: 4,
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        borderRadius: '12px',
        p: 4
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2 }}>
          Questions about Accessibility?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Contact our Accessibility Team at <Link href="mailto:accessibility@circledin.com">support@circledin.com</Link>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Last Updated: January 1, 2025 | Version: 1.0
        </Typography>
        <Typography variant="body2" color="text.secondary">
          © 2025 Circledin LLC. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
}

export default Accessibility;